class EditorialHeader extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.toggle?.addEventListener('click', () => this.setOpen(!this.hasAttribute('data-menu-open')), options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const disclosure = this.querySelector('details[open]');
        if (disclosure) { disclosure.open = false; disclosure.querySelector('summary')?.focus(); event.stopPropagation(); return; }
      }
      if (event.key === 'Escape' && this.hasAttribute('data-menu-open')) {
        this.setOpen(false);
        this.toggle?.focus();
      }
    }, options);
    this.querySelector('nav')?.addEventListener('click', event => {
      if (event.target.closest('a')) this.setOpen(false);
    }, options);
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) { this.setOpen(false); for (const disclosure of this.querySelectorAll('details[open]')) disclosure.open = false; }
    }, options);
    this.querySelectorAll('details').forEach(disclosure => disclosure.addEventListener('toggle', () => {
      if (disclosure.open) for (const sibling of this.querySelectorAll('details[open]')) if (sibling !== disclosure) sibling.open = false;
    }, options));
    this.media = matchMedia('(min-width: 990px)');
    this.media.addEventListener('change', () => this.setOpen(false), options);
  }
  setOpen(open) {
    this.toggleAttribute('data-menu-open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));
    for (const region of document.querySelectorAll('main, .site-footer')) region.inert = open;
  }
  disconnectedCallback() { this.setOpen(false); this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('editorial-header')) customElements.define('editorial-header', EditorialHeader);

class PredictiveFragranceSearch extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.details = this.querySelector('details');
    this.input = this.querySelector('input[type="search"]');
    this.status = this.querySelector('[data-search-status]');
    this.hint = this.status.textContent;
    this.results = this.querySelector('[data-search-results]');
    this.all = this.querySelector('[data-search-all]');
    this.discover = this.querySelector('[data-search-discover]');
    this.cache = new Map(); this.version = 0;
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
    const close = this.querySelector('[data-search-close]'); close.hidden = false;
    close.addEventListener('click', () => this.close(), options);
    this.details.addEventListener('toggle', () => {
      if (this.details.open) {
        this.closest('editorial-header')?.setOpen(false);
        this.input.focus();
        this.schedule();
      } else this.cancel();
    }, options);
    this.input.addEventListener('input', event => { if (!event.isComposing) this.schedule(); }, options);
    this.input.addEventListener('compositionend', () => this.schedule(), options);
    // Touch browsers can blur the input without focusing the tapped link.
    // Keep the results mounted until its native click navigates; close only
    // when another control actually receives focus (outside clicks are handled by the header).
    document.addEventListener('focusin', event => {
      if (this.details.open && !this.contains(event.target)) { this.details.open = false; this.cancel(); }
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.details.open) { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = [...this.results.querySelectorAll('a'), this.all].filter(link => !link.closest('[hidden]'));
      if (!links.length) return;
      const index = links.indexOf(document.activeElement);
      if (event.target !== this.input && index < 0) return;
      event.preventDefault();
      const next = index + (event.key === 'ArrowDown' ? 1 : -1);
      if (event.target === this.input) links[event.key === 'ArrowDown' ? 0 : links.length - 1].focus();
      else if (next < 0 || next >= links.length) this.input.focus();
      else links[next].focus();
    }, options);
  }
  cancel() {
    clearTimeout(this.timer); this.request?.abort(); this.version++;
    this.results?.removeAttribute('aria-busy');
  }
  close() { this.details.open = false; this.cancel(); this.querySelector('summary').focus(); }
  schedule() {
    this.cancel();
    const query = this.input.value.trim().replace(/\s+/g, ' ');
    this.results.hidden = true;
    this.discover.hidden = !!query;
    this.all.hidden = !query;
    const url = new URL(this.querySelector('form').action, location.href);
    url.searchParams.set('q', query); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last');
    this.all.href = url.href;
    if (query.length < 2) { this.status.textContent = this.hint; return; }
    this.status.textContent = this.dataset.loading;
    const version = this.version;
    this.timer = setTimeout(() => this.search(query, version), 250);
  }
  async search(query, version) {
    this.results.setAttribute('aria-busy', 'true');
    try {
      let data = this.cache.get(query.toLowerCase());
      if (!data) {
        this.request = new AbortController();
        const url = new URL(this.dataset.endpoint, location.href);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        data = (await response.json()).resources?.results;
        if (!data || !Array.isArray(data.products)) throw new Error('Invalid search response');
        if (version !== this.version) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query.toLowerCase(), data);
      }
      if (version !== this.version || !this.details.open) return;
      this.render(data);
    } catch (error) {
      if (version === this.version && error.name !== 'AbortError') { this.results.hidden = true; this.status.textContent = this.dataset.error; }
    } finally { if (version === this.version) this.results.removeAttribute('aria-busy'); }
  }
  render(data) {
    const productList = this.querySelector('[data-search-product-list]');
    const collectionList = this.querySelector('[data-search-collection-list]');
    productList.replaceChildren(); collectionList.replaceChildren();
    const make = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text != null) node.textContent = text; return node; };
    const safeLink = value => { try { const url = new URL(value, location.href); return url.origin === location.origin && ['http:', 'https:'].includes(url.protocol) ? url.href : null; } catch { return null; } };
    for (const product of data.products.slice(0, 6)) {
      const href = safeLink(product.url); if (!href) continue;
      const li = make('li'), link = make('a', 'header-search-result'); link.href = href;
      const picture = make('span', 'header-search-image');
      const source = product.featured_image?.url || product.image;
      if (source) {
        try {
          const imageURL = new URL(source, location.href);
          if (['http:', 'https:'].includes(imageURL.protocol)) {
            imageURL.searchParams.set('width', '128');
            const image = make('img'); image.src = imageURL.href; image.alt = ''; image.width = 64; image.height = 72; image.loading = 'lazy'; picture.append(image);
          }
        } catch { /* Keep the quiet placeholder if an image is unavailable. */ }
      }
      const copy = make('span', 'header-search-result-copy');
      copy.append(make('span', 'header-search-brand', product.vendor), make('span', 'header-search-name', product.title));
      const amount = Number(product.price_min ?? product.price);
      const price = make('span', 'header-search-price');
      if (Number.isFinite(amount)) { const formatted = this.money.format(amount); price.textContent = product.price_varies ? this.dataset.from.replace('__PRICE__', formatted) : formatted; }
      if (product.available === false) price.append(make('small', '', this.dataset.sold));
      link.append(picture, copy, price); li.append(link); productList.append(li);
    }
    for (const collection of (data.collections || []).slice(0, 3)) {
      const href = safeLink(collection.url); if (!href) continue;
      const li = make('li'), link = make('a', '', collection.title); link.href = href; li.append(link); collectionList.append(li);
    }
    this.querySelector('[data-search-products]').hidden = !productList.children.length;
    this.querySelector('[data-search-collections]').hidden = !collectionList.children.length;
    const count = productList.children.length + collectionList.children.length;
    this.results.hidden = !count;
    this.status.textContent = count ? this.dataset.count.replace('__COUNT__', String(count)) : this.dataset.empty;
  }
  disconnectedCallback() { this.cancel(); this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('predictive-fragrance-search')) customElements.define('predictive-fragrance-search', PredictiveFragranceSearch);

class EditorialCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.rail = this.querySelector('[data-rail]');
    if (!this.rail) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.previous?.addEventListener('click', () => this.move(-1), options);
    this.next?.addEventListener('click', () => this.move(1), options);
    this.rail.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }, { ...options, passive: true });
    this.rail.addEventListener('keydown', event => {
      if (event.target !== this.rail) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.rail.scrollTo({ left: event.key === 'Home' ? 0 : this.rail.scrollWidth, behavior: 'instant' });
      }
    }, options);
    this.addEventListener('shopify:block:select', event => {
      const item = event.target.closest('li');
      if (item) this.rail.scrollBy({ left: item.getBoundingClientRect().left - this.rail.getBoundingClientRect().left, behavior: 'instant' });
    }, options);
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.rail);
    if (this.rail.hasAttribute('data-fit-height')) {
      [...this.rail.children].forEach(item => this.observer.observe(item));
    }
    this.update();
  }
  move(direction) {
    const first = this.rail.firstElementChild;
    if (!first) return;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap);
    const step = first.getBoundingClientRect().width + gap;
    const count = Math.max(1, Math.floor((this.rail.clientWidth + gap + 1) / step));
    this.rail.scrollBy({ left: direction * count * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  update() {
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const controls = this.querySelector('[data-rail-controls]');
    if (controls) controls.hidden = max < 2;
    if (this.previous) this.previous.disabled = this.rail.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.rail.scrollLeft >= max - 2;
    const bounds = this.rail.getBoundingClientRect();
    const items = [...this.rail.children];
    const visible = items.map((item, index) => ({ index, bounds: item.getBoundingClientRect() }))
      .filter(item => item.bounds.left >= bounds.left - 2 && item.bounds.right <= bounds.right + 2);
    if (this.rail.hasAttribute('data-fit-height') && visible.length) {
      const style = getComputedStyle(this.rail);
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const scrollbar = this.rail.offsetHeight - this.rail.clientHeight;
      const height = `${Math.ceil(Math.max(...visible.map(item => item.bounds.height)) + padding + scrollbar)}px`;
      if (this.rail.style.height !== height) this.rail.style.height = height;
    }
    const count = this.querySelector('[data-rail-count]');
    if (count && visible.length) {
      const value = `${visible[0].index + 1}–${visible.at(-1).index + 1} / ${items.length}`;
      if (count.textContent !== value) count.textContent = value;
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.controller = null;
  }
}
if (!customElements.get('editorial-carousel')) customElements.define('editorial-carousel', EditorialCarousel);

class ScentStories extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[data-story-panel]')];
    if (!this.tabs.length) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.querySelector('[role="tablist"]').hidden = false;
    this.toggleAttribute('data-enhanced', true);
    this.tabs.forEach((tab, index) => {
      this.panels[index].setAttribute('role', 'tabpanel');
      this.panels[index].setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', () => this.select(index), options);
      tab.addEventListener('keydown', event => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else return;
        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      }, options);
    });
    this.addEventListener('shopify:block:select', event => {
      const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail.blockId);
      if (index >= 0) this.select(index);
    }, options);
    this.select(0);
  }
  select(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      this.panels[i].hidden = i !== index;
    });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('scent-stories')) customElements.define('scent-stories', ScentStories);

class ProductImage extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    const secondary = this.querySelector('.product-card__image--secondary');
    if (!secondary) return;
    this.controller = new AbortController();
    const update = () => this.toggleAttribute('data-secondary-ready', secondary.complete && secondary.naturalWidth > 0);
    secondary.addEventListener('load', update, { signal: this.controller.signal });
    secondary.addEventListener('error', () => {
      this.removeAttribute('data-secondary-ready');
      const cue = this.querySelector('.product-card__photo-cue');
      if (cue) cue.hidden = true;
    }, { signal: this.controller.signal });
    update();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('product-image')) customElements.define('product-image', ProductImage);

class QuantityPicker extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.controller = new AbortController();
    this.setAttribute('data-enhanced', '');
    for (const [selector, direction] of [['[data-quantity-minus]', -1], ['[data-quantity-plus]', 1]]) {
      const button = this.querySelector(selector);
      if (!button) continue;
      button.hidden = false;
      button.addEventListener('click', () => {
        const min = Number(this.input.min || 1), max = this.input.max ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || min, step = Number(this.input.step || 1);
        this.input.value = String(Math.min(max, Math.max(min, current + direction * step)));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.controller.signal });
    }
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('quantity-picker')) customElements.define('quantity-picker', QuantityPicker);

class EditorialProduct extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    const select = this.querySelector('[data-variant-select]');
    if (!select) return;
    this.controller = new AbortController();
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0], data = option.dataset;
      this.querySelector('[data-product-price]').textContent = data.price;
      const compare = this.querySelector('[data-product-compare]');
      compare.hidden = !data.compare;
      compare.textContent = data.compare;
      const savingBadge = this.querySelector('[data-product-saving-badge]');
      const savingAmount = this.querySelector('[data-product-saving-amount]');
      if (savingBadge) { savingBadge.textContent = data.savingBadge || ''; savingBadge.hidden = !data.savingBadge; }
      if (savingAmount) { savingAmount.textContent = data.savingAmount || ''; savingAmount.hidden = !data.savingAmount; }
      const button = this.querySelector('[data-product-submit]'), available = data.available === 'true';
      button.disabled = !available;
      this.querySelector('[data-product-submit-label]').textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
      const stock = this.querySelector('[data-product-stock]');
      stock.textContent = available ? stock.dataset.inStock : stock.dataset.soldOut;
      const input = this.querySelector('[name="quantity"]');
      input.min = data.min || '1'; input.step = data.step || '1';
      if (data.max) input.max = data.max; else input.removeAttribute('max');
      input.value = data.min || '1';
      this.querySelector('[data-product-sku]').textContent = data.sku || '';
      this.querySelector('[data-product-sku-row]').hidden = !data.sku;
      this.querySelector('[data-product-option]').textContent = option.textContent.split(' — ')[0];
      this.querySelector('[data-product-status]').textContent = `${option.textContent}. ${stock.textContent}${data.savingAmount ? `. ${data.savingAmount}` : ''}`;
      const url = new URL(location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url);
      if (data.mediaId) this.closest('.product-detail').querySelector('product-gallery')?.selectMedia(data.mediaId);
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('editorial-product')) customElements.define('editorial-product', EditorialProduct);

class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.rail = this.querySelector('[data-gallery-rail]');
    this.thumbnails = [...this.querySelectorAll('[data-gallery-index]')];
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.index = 0;
    this.thumbnails.forEach(link => link.addEventListener('click', event => { event.preventDefault(); this.select(Number(link.dataset.galleryIndex)); }, options));
    this.rail.addEventListener('keydown', event => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.select(event.key === 'Home' ? 0 : event.key === 'End' ? this.rail.children.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    this.rail.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update(Math.round(this.rail.scrollLeft / Math.max(1, this.rail.clientWidth))));
    }, { ...options, passive: true });
    const zoom = this.querySelector('[data-gallery-zoom]');
    if (zoom && this.dialog) {
      zoom.hidden = false;
      zoom.addEventListener('click', () => {
        const source = this.thumbnails[this.index]?.dataset.fullImage || this.rail.children[this.index]?.querySelector('img')?.src;
        if (source) this.querySelector('[data-zoom-image]').src = source;
        this.dialog.showModal();
      }, options);
      this.querySelector('[data-gallery-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, options);
    }
  }
  update(index) {
    this.index = Math.max(0, Math.min(this.rail.children.length - 1, index));
    this.thumbnails.forEach((thumbnail, i) => { if (i === this.index) thumbnail.setAttribute('aria-current', 'true'); else thumbnail.removeAttribute('aria-current'); });
  }
  select(index) { this.update(index); this.rail.scrollTo({ left: this.index * this.rail.clientWidth, behavior: 'instant' }); }
  selectMedia(id) { const index = [...this.rail.children].findIndex(item => item.dataset.mediaId === id); if (index >= 0) this.select(index); }
  disconnectedCallback() { this.controller?.abort(); this.dialog?.close(); cancelAnimationFrame(this.frame); this.controller = null; }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

class ProductShare extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    const button = this.querySelector('[data-copy-link]');
    if (!button || !navigator.clipboard) return;
    this.controller = new AbortController(); button.hidden = false;
    button.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(this.dataset.url); this.querySelector('[data-share-status]').textContent = this.dataset.copied; }
      catch { this.querySelector('[data-share-status]').textContent = this.dataset.copyError; }
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('product-share')) customElements.define('product-share', ProductShare);

class CollectionMotion extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.videos = [...this.querySelectorAll('[data-motion-video]')];
    this.button = this.querySelector('[data-motion-toggle]');
    if (!this.videos.length || typeof IntersectionObserver === 'undefined') return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.preference = matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;
    this.visible = new Set();
    this.pending = new Set();
    this.userPaused = false;
    this.selective = this.hasAttribute('data-selective-motion');
    this.selectedVideo = null;
    this.preference.addEventListener('change', () => this.sync(), options);
    this.connection?.addEventListener('change', () => this.sync(), options);
    document.addEventListener('visibilitychange', () => this.sync(), options);
    document.addEventListener('intro:change', () => this.sync(), options);
    this.button?.addEventListener('click', () => { this.userPaused = !this.userPaused; this.sync(); }, options);
    this.videos.forEach(video => {
      video.muted = true;
      if (this.selective) {
        const card = video.closest('[data-motion-card], .product-card');
        const preview = card?.querySelector('[data-product-preview]');
        card?.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.select(video); }, options);
        card?.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && this.selectedVideo === video) this.select(null); }, options);
        card?.addEventListener('focusin', event => { if (event.target.matches(':focus-visible')) this.select(video); }, options);
        card?.addEventListener('focusout', event => { if (!card.contains(event.relatedTarget) && this.selectedVideo === video) this.select(null); }, options);
        preview?.addEventListener('click', () => this.select(video), options);
      }
      video.addEventListener('playing', () => {
        if (this.shouldPlay(video)) video.setAttribute('data-ready', '');
        else video.pause();
      }, options);
      video.addEventListener('error', () => video.removeAttribute('data-ready'), options);
    });
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= .1) this.visible.add(entry.target);
        else {
          this.visible.delete(entry.target);
          if (this.selective && this.selectedVideo === entry.target) this.selectedVideo = null;
        }
      });
      this.sync();
    }, { threshold: [0, .1] });
    this.videos.forEach(video => this.observer.observe(video));
    this.sync();
  }
  select(video) {
    this.selectedVideo = video;
    this.sync();
  }
  shouldPlay(video) {
    return !!this.controller && !this.preference.matches && !this.connection?.saveData && !document.hidden && !document.documentElement?.dataset.introState && !this.userPaused && this.visible.has(video) && (!this.selective || this.selectedVideo === video);
  }
  sync() {
    if (this.button) {
      this.button.hidden = this.preference.matches || !!this.connection?.saveData || (this.selective && !this.selectedVideo);
      this.button.setAttribute('aria-pressed', String(this.userPaused));
      this.button.textContent = this.userPaused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
      this.button.setAttribute('aria-label', this.button.textContent);
    }
    this.videos.forEach(video => {
      if (this.selective) {
        const selected = video === this.selectedVideo;
        const card = video.closest('[data-motion-card], .product-card');
        card?.toggleAttribute('data-selected', selected);
        card?.querySelector('[data-product-preview]')?.setAttribute('aria-pressed', String(selected));
        if (!selected) video.removeAttribute('data-ready');
      }
      if (!this.shouldPlay(video)) { video.pause(); return; }
      if (!video.getAttribute('src')) { video.src = video.dataset.srcMobile && matchMedia('(max-width: 749px)').matches ? video.dataset.srcMobile : video.dataset.src; video.load(); }
      if (!video.paused || this.pending.has(video)) return;
      this.pending.add(video);
      Promise.resolve(video.play()).catch(() => video.removeAttribute('data-ready')).finally(() => this.pending.delete(video));
    });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = null;
    this.videos?.forEach(video => { video.pause(); video.removeAttribute('src'); video.removeAttribute('data-ready'); video.load(); });
    this.visible?.clear();
  }
}
if (!customElements.get('collection-motion')) customElements.define('collection-motion', CollectionMotion);
